/* ============================================================================
   ProofStore — the shared "database" behind The Sticker Lab ecosystem.
   Both My Sticker Lab (customer) and Control Center (admin) read & write here.
   Backed by localStorage so the whole proof loop actually works and stays in
   sync across pages/tabs. Plain global — no build step.

   The loop:
     customer orders → order lands in Control Center
        → admin creates a proof → Send Proof
        → email queued + customer notification + pending approval
        → customer approves → order auto-moves to Production
   ============================================================================ */
(function (root) {
  'use strict';

  var K = {
    orders:   'stickr_orders',
    proofs:   'stickr_proofs',
    activity: 'stickr_activity',      // customer-facing activity feed
    notifs:   'stickr_notifs',        // customer notifications
    email:    'stickr_last_email',    // most-recent "proof ready" email
    user:     'stickr_user',
    pw:       'stickr_pw_overrides',   // { username: newPassword } from password resets
    reset:    'stickr_reset_req',      // pending password-reset request
    accounts: 'stickr_custom_accounts',// self-serve accounts created via sign-up
    welcome:  'stickr_welcome_pending',// first-run welcome + tour flag
    seeded:   'stickr_seeded_v1',
  };

  var THUMBS = {
    coffee:'assets/mysl/mysl-coffee.png', summit:'assets/mysl/mysl-summit.png',
    paws:'assets/mysl/mysl-paws.png', volt:'assets/mysl/mysl-volt.png',
    leaf:'assets/mysl/mysl-leaf.png', nova:'assets/mysl/mysl-nova.png',
    harbor:'assets/mysl/mysl-harbor.png', bloom:'assets/mysl/mysl-bloom.png',
  };

  // ---- low-level -----------------------------------------------------------
  function read(key, fallback) {
    try { var v = localStorage.getItem(key); return v == null ? fallback : JSON.parse(v); }
    catch (e) { return fallback; }
  }
  function write(key, val) {
    try { localStorage.setItem(key, JSON.stringify(val)); } catch (e) {}
    emit();
  }
  function uid(p) { return (p || 'id') + '_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6); }
  function now() { return Date.now(); }

  // ---- change notification (same-page custom event + cross-tab storage) ----
  var LISTENERS = [];
  function emit() {
    LISTENERS.forEach(function (cb) { try { cb(); } catch (e) {} });
    try { root.dispatchEvent(new CustomEvent('proofstore:change')); } catch (e) {}
  }
  function onChange(cb) {
    LISTENERS.push(cb);
    var storageCb = function (e) { if (!e.key || e.key.indexOf('stickr_') === 0) cb(); };
    root.addEventListener('storage', storageCb);
    return function () {
      LISTENERS = LISTENERS.filter(function (x) { return x !== cb; });
      root.removeEventListener('storage', storageCb);
    };
  }

  // ---- roles / auth --------------------------------------------------------
  // Customer → My Sticker Lab.  Staff / Admin → Control Center.
  var ACCOUNTS = [
    { username:'ryan',  password:'test',  role:'customer', name:'Ryan',   email:'ryan@atlascraft.co' },
    { username:'admin', password:'admin', role:'admin',    name:'Alex',   email:'alex@thestickerlab.com' },
    { username:'staff', password:'staff', role:'staff',    name:'Jordan', email:'jordan@thestickerlab.com' },
  ];
  // Self-serve accounts created through the sign-up form live in localStorage
  // and are merged with the built-in demo accounts everywhere auth looks.
  function customAccounts() { var a = read(K.accounts, []); return Array.isArray(a) ? a : []; }
  function allAccounts() { return ACCOUNTS.concat(customAccounts()); }
  function authenticate(u, p) {
    u = (u || '').trim().toLowerCase();
    var ov = read(K.pw, {}) || {};
    var a = allAccounts().filter(function (x) {
      var pw = (ov[x.username] != null) ? ov[x.username] : x.password;
      return x.username === u && pw === p;
    })[0];
    return a ? { username:a.username, name:a.name, role:a.role, email:a.email } : null;
  }

  // ---- self-serve account creation -----------------------------------------
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  function createAccount(info) {
    info = info || {};
    var name = (info.name || '').trim();
    var email = (info.email || '').trim().toLowerCase();
    var username = (info.username || '').trim().toLowerCase();
    var pw = info.password || '';
    if (!name)                    return { ok:false, error:'name',      msg:"What should we call you?" };
    if (!EMAIL_RE.test(email))    return { ok:false, error:'email',     msg:"That email doesn't look right." };
    if (!username || username.length < 3) return { ok:false, error:'username', msg:"Pick a username with at least 3 characters." };
    if (!/^[a-z0-9_.]+$/.test(username))  return { ok:false, error:'username', msg:"Usernames can use letters, numbers, dots and underscores." };
    if (pw.length < 6)            return { ok:false, error:'password',  msg:"Passwords need at least 6 characters." };
    if (allAccounts().some(function (x) { return x.username === username; }))
      return { ok:false, error:'username', msg:"That username is taken — try another." };
    if (allAccounts().some(function (x) { return (x.email || '').toLowerCase() === email; }))
      return { ok:false, error:'email', msg:"An account already exists with that email." };
    var rec = { username:username, password:pw, role:'customer', name:name, email:email, createdAt:now() };
    var list = customAccounts(); list.push(rec); write(K.accounts, list);
    return { ok:true, account:{ username:username, name:name, role:'customer', email:email } };
  }
  // First-run welcome + tour flag (set at sign-up, consumed by My Sticker Lab).
  function flagWelcome() { write(K.welcome, true); }
  function consumeWelcome() { var v = read(K.welcome, false); if (v) write(K.welcome, false); return !!v; }

  // ---- password reset ------------------------------------------------------
  function accountByEmail(email) {
    email = (email || '').trim().toLowerCase();
    return allAccounts().filter(function (x) { return (x.email || '').toLowerCase() === email; })[0] || null;
  }
  // Step 1 — customer asks for a reset link. Returns the account (or null) and
  // "sends" an email by storing the pending request + a token.
  function requestReset(email) {
    var a = accountByEmail(email);
    if (!a) return { ok:false };
    var token = uid('rst');
    write(K.reset, { username:a.username, email:a.email, name:a.name, token:token, at:now(), used:false });
    return { ok:true, account:{ username:a.username, name:a.name, email:a.email }, token:token };
  }
  function pendingReset() { return read(K.reset, null); }
  // Step 2 — customer sets a new password on the reset page (validated by email).
  function resetPassword(email, newPw) {
    var a = accountByEmail(email);
    if (!a) return { ok:false, error:'no_account' };
    if (!newPw || newPw.length < 4) return { ok:false, error:'weak' };
    var ov = read(K.pw, {}) || {}; ov[a.username] = newPw; write(K.pw, ov);
    var r = pendingReset(); if (r && r.username === a.username) { r.used = true; write(K.reset, r); }
    var acc = { username:a.username, name:a.name, role:a.role, email:a.email };
    return { ok:true, account:acc };
  }
  function roleHome(role) { return (role === 'admin' || role === 'staff') ? 'Control Center.html' : 'My Sticker Lab.html'; }
  function currentUser() { return read(K.user, null); }
  function setUser(u) { write(K.user, u); }
  function logout() { write(K.user, null); }
  function isStaff(u) { u = u || currentUser(); return !!u && (u.role === 'admin' || u.role === 'staff'); }

  // ---- getters -------------------------------------------------------------
  function orders()   { return read(K.orders, []); }
  function proofs()   { return read(K.proofs, []); }
  function activity() { return read(K.activity, []); }
  function notifs()   { return read(K.notifs, []); }
  function lastEmail(){ return read(K.email, null); }

  function proof(id)  { return proofs().filter(function (x) { return x.id === id; })[0] || null; }
  function order(id)  { return orders().filter(function (x) { return x.id === id; })[0] || null; }

  // proofs a customer still needs to act on
  function pendingApprovals() {
    return proofs().filter(function (p) { return p.status === 'sent' || p.status === 'viewed'; })
                   .sort(function (a, b) { return (b.sentAt || 0) - (a.sentAt || 0); });
  }
  // proofs that need admin attention (nothing sent yet, or customer asked for changes)
  function queue() {
    return proofs().filter(function (p) { return p.status === 'draft' || p.status === 'ready' || p.status === 'changes_requested'; });
  }
  function unreadNotifs() { return notifs().filter(function (n) { return !n.read; }).length; }

  // ---- mutations -----------------------------------------------------------
  function _writeProofs(arr) { write(K.proofs, arr); }
  function _updateProof(id, patch) {
    var arr = proofs().map(function (p) { return p.id === id ? Object.assign({}, p, patch) : p; });
    _writeProofs(arr);
    return arr.filter(function (p) { return p.id === id; })[0];
  }
  function _pushTimeline(id, type) {
    var p = proof(id); if (!p) return;
    var tl = (p.timeline || []).concat([{ type: type, at: now() }]);
    _updateProof(id, { timeline: tl });
  }
  function _updateOrder(id, patch) {
    write(K.orders, orders().map(function (o) { return o.id === id ? Object.assign({}, o, patch) : o; }));
  }

  function addActivity(ev) {
    var a = activity().concat([Object.assign({ id: uid('act'), at: now() }, ev)]);
    write(K.activity, a);
  }
  function addNotif(n) {
    var arr = notifs().concat([Object.assign({ id: uid('ntf'), at: now(), read: false }, n)]);
    write(K.notifs, arr);
  }
  function markNotifsRead() { write(K.notifs, notifs().map(function (n) { return Object.assign({}, n, { read: true }); })); }

  // admin: create a proof for an order (draft — not yet sent)
  function createProof(orderId, data) {
    var o = order(orderId); if (!o) return null;
    var p = {
      id: uid('prf'), orderId: orderId, orderNo: o.orderNo,
      customerName: o.customerName, customerEmail: o.customerEmail,
      designName: o.designName, artworkThumb: o.artworkThumb,
      originalFile: o.originalFile || null,
      proofThumb: (data && data.proofThumb) || o.artworkThumb,
      status: 'draft', customerNotes: o.customerNotes || '', internalNotes: '',
      createdAt: now(), sentAt: null, decidedAt: null, version: 1,
      timeline: [{ type: 'proof_created', at: now() }],
    };
    _writeProofs(proofs().concat([p]));
    _updateOrder(orderId, { stage: 'proofing', proofId: p.id });
    return p;
  }
  function replaceProof(id, thumb) {
    var p = proof(id); if (!p) return;
    _updateProof(id, { proofThumb: thumb, version: (p.version || 1) + 1 });
    _pushTimeline(id, 'proof_replaced');
  }
  function setInternalNotes(id, txt) { _updateProof(id, { internalNotes: txt }); }
  function markReady(id) { _updateProof(id, { status: 'ready' }); }

  // admin: SEND PROOF → this is the money moment.
  function sendProof(id) {
    var p = proof(id); if (!p) return;
    _updateProof(id, { status: 'sent', sentAt: now() });
    _pushTimeline(id, 'proof_sent');
    _updateOrder(p.orderId, { stage: 'awaiting_approval' });
    // customer notification (they don't miss these)
    addNotif({
      kind: 'proof', proofId: id, title: 'Your proof is ready to approve',
      body: p.designName + ' · Order ' + p.orderNo, cta: 'Review proof',
    });
    // activity feed entry
    addActivity({ type: 'proof_sent', title: 'Proof ready for review', sub: p.designName, proofId: id });
    // queue the "proof ready" email for the mockup page
    write(K.email, {
      to: p.customerEmail, toName: p.customerName, designName: p.designName,
      orderNo: p.orderNo, proofThumb: p.proofThumb, proofId: id, sentAt: now(),
    });
  }

  // customer opened the proof
  function markViewed(id) {
    var p = proof(id); if (!p || p.status !== 'sent') return;
    _updateProof(id, { status: 'viewed' });
    _pushTimeline(id, 'customer_viewed');
  }

  // customer approves → order auto-moves to Production
  function approveProof(id) {
    var p = proof(id); if (!p) return;
    _updateProof(id, { status: 'approved', decidedAt: now() });
    _pushTimeline(id, 'customer_approved');
    _updateOrder(p.orderId, { stage: 'production' });
    addActivity({ type: 'proof_approved', title: 'Proof approved', sub: p.designName, proofId: id });
    markProofNotifsDone(id);
  }

  // customer requests changes
  function requestChanges(id, note) {
    var p = proof(id); if (!p) return;
    _updateProof(id, { status: 'changes_requested', decidedAt: now(), changeNote: note || '' });
    _pushTimeline(id, 'changes_requested');
    _updateOrder(p.orderId, { stage: 'proofing' });
    addActivity({ type: 'changes_requested', title: 'Changes requested', sub: p.designName, proofId: id });
    markProofNotifsDone(id);
  }

  // admin final gate (after approval, physically send to production line)
  function approveForProduction(id) {
    var p = proof(id); if (!p) return;
    _updateProof(id, { status: 'in_production', productionAt: now() });
    _pushTimeline(id, 'sent_to_production');
    _updateOrder(p.orderId, { stage: 'production' });
  }
  function rejectOrder(id)  { var p = proof(id); if (p) _updateOrder(p.orderId, { stage: 'rejected' }); }
  function archiveProof(id) { _updateProof(id, { archived: true }); }

  // customer: request a free proof from the builder → creates an order in the queue (stage 'new')
  function submitProofRequest(data) {
    data = data || {};
    var nums = orders().map(function (o) { return parseInt((o.orderNo || '#0').replace('#', ''), 10) || 0; });
    var next = (nums.length ? Math.max.apply(null, nums) : 1040) + 1;
    var o = {
      id: uid('ord'), orderNo: '#' + next,
      customerName: data.customerName || 'Customer', customerEmail: data.customerEmail || '',
      designName: data.designName || 'Custom sticker', artworkThumb: data.artworkThumb || null,
      originalFile: data.originalFile || null,
      qty: data.qty || 0, spec: data.spec || '', total: data.total || '—',
      placedAt: now(), stage: 'new', customerNotes: data.customerNotes || '', proofRequest: true,
    };
    write(K.orders, orders().concat([o]));
    addActivity({ type: 'order_placed', title: 'New proof request', sub: o.designName + ' · ' + o.customerName, orderId: o.id });
    addNotif({ kind: 'request', orderId: o.id, title: 'Proof request received',
      body: o.designName + ' · we\u2019ll email your proof shortly', cta: 'View' });
    return o;
  }

  function markProofNotifsDone(proofId) {
    write(K.notifs, notifs().map(function (n) { return n.proofId === proofId ? Object.assign({}, n, { read: true, done: true }) : n; }));
  }

  // ---- seed demo data ------------------------------------------------------
  function seed(force) {
    if (!force && read(K.seeded, false)) return;
    var t = now(), DAY = 86400000;
    var seedOrders = [
      { id:'ord_1041', orderNo:'#1041', customerName:'Ryan', customerEmail:'ryan@atlascraft.co',
        designName:'Atlas Craft Coffee', artworkThumb:THUMBS.coffee, qty:100, spec:'3.0″ · Vinyl Matte',
        total:'$89.00', placedAt:t-2*DAY, stage:'new',
        customerNotes:'Keep the die-cut tight to the cup handle please.' },
      { id:'ord_1042', orderNo:'#1042', customerName:'Maya Ortega', customerEmail:'maya@riverside.co',
        designName:'Riverside Market', artworkThumb:THUMBS.leaf, qty:250, spec:'2.5″ · Clear Matte',
        total:'$168.00', placedAt:t-1*DAY, stage:'new',
        customerNotes:'Weekend market — need these by Friday if possible.' },
      { id:'ord_1043', orderNo:'#1043', customerName:'Volt Promo', customerEmail:'promo@voltgear.com',
        designName:'Volt Energy', artworkThumb:THUMBS.volt, qty:500, spec:'3.0″ · Vinyl Matte',
        total:'$290.00', placedAt:t-6*3600000, stage:'new',
        customerNotes:'High contrast — should read from across the room.' },
    ];
    write(K.orders, seedOrders);

    // one proof already sent to Ryan and waiting — so the customer lab feels alive
    var p1 = {
      id:'prf_seed1', orderId:'ord_1041', orderNo:'#1041',
      customerName:'Ryan', customerEmail:'ryan@atlascraft.co',
      designName:'Atlas Craft Coffee', artworkThumb:THUMBS.coffee, proofThumb:THUMBS.coffee,
      status:'sent', customerNotes:'Keep the die-cut tight to the cup handle please.',
      internalNotes:'Bumped contrast +8, tightened cut line around handle.',
      createdAt:t-20*3600000, sentAt:t-18*3600000, decidedAt:null, version:1,
      timeline:[{type:'proof_created',at:t-20*3600000},{type:'proof_sent',at:t-18*3600000}],
    };
    _writeProofs([p1]);
    _updateOrder('ord_1041', { stage:'awaiting_approval', proofId:'prf_seed1' });

    write(K.notifs, [
      { id:'ntf_seed1', kind:'proof', proofId:'prf_seed1', read:false,
        title:'Your proof is ready to approve', body:'Atlas Craft Coffee · Order #1041',
        cta:'Review proof', at:t-18*3600000 },
    ]);

    write(K.activity, [
      { id:'act1', type:'proof_sent',   title:'Proof ready for review', sub:'Atlas Craft Coffee',  at:t-18*3600000 },
      { id:'act2', type:'created',      title:'Design created',          sub:'Green Oaks Labels',   at:t-3*DAY },
      { id:'act3', type:'reordered',    title:'Reordered',               sub:'Shipping Labels',     at:t-7*DAY },
      { id:'act4', type:'logo',         title:'Uploaded logo',           sub:'Brand Assets',        at:t-9*DAY },
    ]);

    write(K.seeded, true);
    emit();
  }
  function resetAll() {
    [K.orders,K.proofs,K.activity,K.notifs,K.email,K.seeded].forEach(function(k){ try{localStorage.removeItem(k);}catch(e){} });
    seed(true);
  }

  // ---- date helpers (shared) ----------------------------------------------
  function relTime(ts) {
    if (!ts) return 'just now';
    var s = (now() - ts) / 1000;
    if (s < 60) return 'just now';
    if (s < 3600) return Math.round(s/60) + ' min ago';
    if (s < 86400) { var h=Math.round(s/3600); return h + ' hour' + (h>1?'s':'') + ' ago'; }
    var d = Math.round(s/86400);
    if (d === 1) return 'Yesterday';
    if (d < 14) return d + ' days ago';
    return Math.round(d/7) + ' week' + (Math.round(d/7)>1?'s':'') + ' ago';
  }
  function fmtDate(ts) { try { return new Date(ts||now()).toLocaleDateString(undefined,{month:'short',day:'numeric',year:'numeric'}); } catch(e){ return '—'; } }

  // ---- original-artwork file helpers ---------------------------------------
  // A safe, order-numbered filename e.g. "1042-Riverside-Market.png".
  function fileLabel(rec, fallbackExt) {
    rec = rec || {};
    var no = String(rec.orderNo || '').replace(/[^0-9]/g, '') || 'file';
    var name = String(rec.designName || 'artwork').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');
    var of = rec.originalFile;
    var ext = fallbackExt || 'png';
    if (of && of.name && of.name.indexOf('.') > -1) ext = of.name.split('.').pop().toLowerCase();
    else if (of && of.dataUrl && of.dataUrl.indexOf('data:') === 0) {
      var m = of.dataUrl.match(/data:([^;]+)/); if (m && m[1]) { var t = m[1].split('/').pop(); if (t) ext = (t === 'jpeg' ? 'jpg' : t); }
    }
    return no + '-' + name + '.' + ext;
  }
  // Returns { name, dataUrl } for the customer's original file, falling back to
  // the design preview so there is always something to download in the demo.
  function originalFileOf(rec) {
    rec = rec || {};
    var of = rec.originalFile;
    if (of && of.dataUrl) return { name: of.name || fileLabel(rec), dataUrl: of.dataUrl, real: true };
    if (rec.artworkThumb) return { name: fileLabel(rec), dataUrl: rec.artworkThumb, real: false };
    return null;
  }
  function fmtTime(ts) { try { return new Date(ts||now()).toLocaleString(undefined,{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'}); } catch(e){ return '—'; } }

  var STAGE_LABEL = {
    new:'New order', proofing:'Proofing', awaiting_approval:'Awaiting approval',
    approved:'Approved', production:'In production', shipping:'Shipping',
    rejected:'Rejected', delivered:'Delivered',
  };

  root.ProofStore = {
    keys:K, THUMBS:THUMBS, STAGE_LABEL:STAGE_LABEL,
    onChange:onChange,
    // auth
    authenticate:authenticate, roleHome:roleHome, currentUser:currentUser, setUser:setUser, logout:logout, isStaff:isStaff,
    accountByEmail:accountByEmail, requestReset:requestReset, pendingReset:pendingReset, resetPassword:resetPassword,
    createAccount:createAccount, flagWelcome:flagWelcome, consumeWelcome:consumeWelcome,
    // getters
    orders:orders, proofs:proofs, activity:activity, notifs:notifs, lastEmail:lastEmail,
    proof:proof, order:order, pendingApprovals:pendingApprovals, queue:queue, unreadNotifs:unreadNotifs,
    // mutations
    createProof:createProof, replaceProof:replaceProof, setInternalNotes:setInternalNotes, markReady:markReady,
    sendProof:sendProof, markViewed:markViewed, approveProof:approveProof, requestChanges:requestChanges,
    approveForProduction:approveForProduction, rejectOrder:rejectOrder, archiveProof:archiveProof, submitProofRequest:submitProofRequest,
    addActivity:addActivity, addNotif:addNotif, markNotifsRead:markNotifsRead, markProofNotifsDone:markProofNotifsDone,
    // lifecycle
    seed:seed, resetAll:resetAll,
    // utils
    relTime:relTime, fmtDate:fmtDate, fmtTime:fmtTime, uid:uid,
    fileLabel:fileLabel, originalFileOf:originalFileOf,
  };

  // Storefront store: do NOT auto-seed demo orders/proofs. This file shares localStorage
  // keys with proof-store-app.js (the app store) but uses non-flat `assets/mysl/*.png`
  // thumbnail paths that 404 on Shopify's flat asset store. Seeding here poisoned the shared
  // `stickr_proofs` that My Stickr Lab / Control Center read (broken proof preview). The
  // storefront only needs the account/cart API (currentUser/setUser/cart), never the seeded
  // demo data — the apps (proof-store-app.js, flat paths) own seeding. See DECISION_LOG D-037.
  // Manual seed()/resetAll() remain callable.
})(window);
